import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, CheckCircle2, Circle, Loader2, TrendingUp } from "lucide-react";
import {
  fetchFullPremiumSchedule,
  markPremiumPaid,
  unmarkPremiumPaid,
} from "../lib/api";
import { normalizeDetail } from "../lib/errors";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const TYPE_LABEL = {
  term_life: "Term",
  life_insurance: "Life",
  health: "Health",
  accident: "Accident",
  two_wheeler: "2W",
  four_wheeler: "4W",
  other: "Other",
};

const TYPE_COLOR = {
  term_life: "border-gold/40 bg-gold/10 text-gold",
  life_insurance: "border-orange-400/40 bg-orange-400/10 text-orange-300",
  health: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
  accident: "border-purple-400/40 bg-purple-400/10 text-purple-300",
  two_wheeler: "border-sky-400/40 bg-sky-400/10 text-sky-300",
  four_wheeler: "border-blue-400/40 bg-blue-400/10 text-blue-300",
  other: "border-border bg-elevated/50 text-muted",
};

function fmtINR(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n));
}

/**
 * Full future-premium calendar. Backend returns every payment from today until each
 * policy's PPT ends (capped at 40 years). We group by year → month and render as a
 * scrollable timeline card so users can plan cash flow for the whole schedule.
 */
export function PremiumCalendarPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [horizonYears, setHorizonYears] = useState(15);
  // Item 4 (13-Feb-2026): default to future-only. When a member adds a policy that started
  // years ago, they don't want to see 5 years of past premiums to mark paid one-by-one —
  // past is assumed paid. Members can flip this to view + retro-mark historical premiums.
  const [showPast, setShowPast] = useState(false);
  const [busyKey, setBusyKey] = useState(null); // `${policy_id}:${due_date}` while toggling

  const reload = useCallback(() => {
    setLoading(true);
    fetchFullPremiumSchedule(horizonYears, showPast)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [horizonYears, showPast]);

  useEffect(() => { reload(); }, [reload]);

  // Optimistic toggle — flip the local `paid` flag before the API completes so the UI feels
  // instant. On error, revert and toast. On success, keep the optimistic state (server
  // already agrees) — no reload needed.
  const togglePaid = useCallback(async (item) => {
    const key = `${item.policy_id}:${item.due_date}`;
    setBusyKey(key);
    const nextPaid = !item.paid;
    setItems((prev) => prev.map((it) =>
      it.policy_id === item.policy_id && it.due_date === item.due_date ? { ...it, paid: nextPaid } : it
    ));
    try {
      if (nextPaid) await markPremiumPaid(item.policy_id, item.due_date);
      else await unmarkPremiumPaid(item.policy_id, item.due_date);
    } catch (err) {
      // Revert on failure.
      setItems((prev) => prev.map((it) =>
        it.policy_id === item.policy_id && it.due_date === item.due_date ? { ...it, paid: !nextPaid } : it
      ));
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't update payment status."));
    } finally {
      setBusyKey(null);
    }
  }, []);

  // Group items by year, then by month within that year.
  const grouped = useMemo(() => {
    const byYear = new Map();
    for (const it of items) {
      const d = new Date(`${it.due_date}T00:00:00`);
      const y = d.getFullYear();
      const m = d.getMonth();
      if (!byYear.has(y)) byYear.set(y, new Map());
      const monthMap = byYear.get(y);
      if (!monthMap.has(m)) monthMap.set(m, []);
      monthMap.get(m).push(it);
    }
    return byYear;
  }, [items]);

  const yearTotals = useMemo(() => {
    const totals = new Map();
    for (const it of items) {
      const y = new Date(`${it.due_date}T00:00:00`).getFullYear();
      totals.set(y, (totals.get(y) || 0) + (it.amount || 0));
    }
    return totals;
  }, [items]);

  const grandTotal = useMemo(
    () => items.reduce((s, it) => s + (it.amount || 0), 0),
    [items]
  );

  // Paid vs pending split for the summary strip.
  const { paidTotal, paidCount } = useMemo(() => {
    let paidTotal = 0;
    let paidCount = 0;
    for (const it of items) {
      if (it.paid) {
        paidTotal += it.amount || 0;
        paidCount += 1;
      }
    }
    return { paidTotal, paidCount };
  }, [items]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 lg:py-10" data-testid="premium-calendar-page">
      <div className="flex flex-col gap-2">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold">
          <span className="h-px w-6 bg-gold/60" />
          Premium Calendar
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-serif text-3xl text-text sm:text-4xl">
            Every premium, <span className="text-gold">planned ahead</span>
          </h1>
          <Link
            to="/app/policies"
            className="text-sm text-muted underline-offset-4 hover:text-text hover:underline"
          >
            ← back to policies
          </Link>
        </div>
        <p className="max-w-2xl text-sm text-muted">
          Full future schedule for all policies — from today until each policy&apos;s
          premium-paying term ends. Adjust the horizon to zoom in or out.
        </p>
      </div>

      {/* Summary strip */}
      <div className="mt-6 grid gap-3 sm:grid-cols-4">
        <StatTile
          icon={Calendar}
          label={`Premiums (next ${horizonYears} yrs)`}
          value={loading ? "…" : `${items.length}`}
          testId="stat-count"
        />
        <StatTile
          icon={TrendingUp}
          label={`Total outflow`}
          value={loading ? "…" : `₹ ${fmtINR(grandTotal)}`}
          testId="stat-total"
        />
        <StatTile
          icon={CheckCircle2}
          label={`Paid so far`}
          value={loading ? "…" : `${paidCount} · ₹ ${fmtINR(paidTotal)}`}
          testId="stat-paid"
        />
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-elevated/40 p-4 text-sm text-muted">
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-widest text-[10px]">Horizon</span>
            <select
              value={horizonYears}
              onChange={(e) => setHorizonYears(Number(e.target.value))}
              data-testid="horizon-select"
              className="rounded-md border border-border bg-card px-2 py-1 text-text"
            >
              {[5, 10, 15, 25, 40].map((y) => (
                <option key={y} value={y}>{y} years</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs" data-testid="show-past-toggle">
            <input
              type="checkbox"
              checked={showPast}
              onChange={(e) => setShowPast(e.target.checked)}
              className="accent-gold"
            />
            Include past (retro-mark historical premiums)
          </label>
        </div>
      </div>

      {loading && (
        <div className="mt-8 flex items-center justify-center gap-2 rounded-lg border border-border p-10 text-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Building your calendar…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="mt-8 rounded-lg border border-dashed border-border p-10 text-center text-muted">
          No upcoming premium payments were found. Add a policy with a start date + PPT
          + amount to see it scheduled here.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="mt-6 space-y-4">
          {[...grouped.entries()].map(([year, monthMap]) => (
            <YearBlock
              key={year}
              year={year}
              monthMap={monthMap}
              yearTotal={yearTotals.get(year) || 0}
              onTogglePaid={togglePaid}
              busyKey={busyKey}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, testId }) {
  return (
    <div
      className="rounded-lg border border-border bg-elevated/40 p-4"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 text-xl font-semibold text-text">{value}</div>
    </div>
  );
}

function YearBlock({ year, monthMap, yearTotal, onTogglePaid, busyKey }) {
  const monthEntries = [...monthMap.entries()].sort((a, b) => a[0] - b[0]);
  return (
    <div
      className="overflow-hidden rounded-2xl border border-gold/20 bg-elevated/30"
      data-testid={`year-block-${year}`}
    >
      <div className="flex items-center justify-between border-b border-border bg-card/60 px-5 py-3">
        <div className="font-serif text-xl text-gold">{year}</div>
        <div className="text-sm text-muted">
          {[...monthMap.values()].reduce((s, ar) => s + ar.length, 0)} payments · ₹{" "}
          <span className="text-text">{fmtINR(yearTotal)}</span>
        </div>
      </div>
      <div className="grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3">
        {monthEntries.map(([m, list]) => (
          <MonthCell key={m} month={m} list={list} onTogglePaid={onTogglePaid} busyKey={busyKey} />
        ))}
      </div>
    </div>
  );
}

function MonthCell({ month, list, onTogglePaid, busyKey }) {
  const monthTotal = list.reduce((s, it) => s + (it.amount || 0), 0);
  return (
    <div className="rounded-lg border border-border/70 bg-card/40 p-3">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-sm font-medium text-text">{MONTHS[month]}</div>
        <div className="text-xs text-muted">₹ {fmtINR(monthTotal)}</div>
      </div>
      <div className="space-y-1.5">
        {list.map((it) => {
          const key = `${it.policy_id}:${it.due_date}`;
          const busy = busyKey === key;
          return (
            <div
              key={key}
              className={`flex items-center justify-between gap-2 text-xs ${it.paid ? "opacity-60" : ""}`}
              data-testid={`premium-row-${it.policy_id}-${it.due_date}`}
            >
              <div className="flex min-w-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => !busy && onTogglePaid(it)}
                  disabled={busy}
                  aria-label={it.paid ? "Mark as unpaid" : "Mark as paid"}
                  data-testid={`toggle-paid-${it.policy_id}-${it.due_date}`}
                  className={`shrink-0 rounded-full p-0.5 transition ${
                    it.paid
                      ? "text-emerald-400 hover:text-emerald-300"
                      : "text-muted hover:text-gold"
                  } ${busy ? "cursor-wait" : "cursor-pointer"}`}
                >
                  {it.paid ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                </button>
                <span
                  className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${
                    TYPE_COLOR[it.policy_type] || TYPE_COLOR.other
                  }`}
                >
                  {TYPE_LABEL[it.policy_type] || "Other"}
                </span>
                <span className={`truncate ${it.paid ? "text-muted line-through" : "text-text"}`} title={it.policy_name}>
                  {it.policy_name}
                </span>
              </div>
              <span className={`whitespace-nowrap font-medium ${it.paid ? "text-emerald-400" : "text-gold"}`}>
                ₹{fmtINR(it.amount)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PremiumCalendarPage;
